import React, { useState, useRef, useCallback } from 'react';
import TasksTable from './TasksTable';
import BulkActionBar from './BulkActionBar';

/**
 * Wraps TasksTable with bulk-select state + the floating BulkActionBar so every
 * task-table surface (project main list, project sections, etc.) gets identical
 * multi-select behaviour.
 *
 * The checkbox column itself stays opt-in: TasksTable renders a "Select tasks"
 * toggle (gated by `showBulkSelect`) and only shifts the layout once the user
 * turns it on. This wrapper just owns the selection state and renders the
 * action bar - it does not force the column open.
 *
 * Any extra props are forwarded straight to TasksTable.
 */
const BulkSelectableTable = ({
  projects = [],
  readOnly = false,
  // Called after a bulk mutation (update/delete) so the parent can refetch.
  onBulkUpdate,
  ...tableProps
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectedTasksRef = useRef([]);
  const metaRef = useRef({ customStatuses: [], projects: [] });

  const handleSelectionChange = useCallback((ids, selectedTasks, meta) => {
    setSelectedIds(new Set(ids));
    selectedTasksRef.current = selectedTasks || [];
    if (meta) metaRef.current = meta;
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return (
    <>
      <TasksTable
        {...tableProps}
        projects={projects}
        readOnly={readOnly}
        showBulkSelect={!readOnly}
        onSelectionChange={!readOnly ? handleSelectionChange : undefined}
      />
      {!readOnly && selectedIds.size > 0 && (
        <BulkActionBar
          selectedIds={selectedIds}
          selectedTasks={selectedTasksRef.current}
          meta={metaRef.current}
          projects={projects}
          onClear={clearSelection}
          onUpdate={onBulkUpdate}
          onDelete={() => { onBulkUpdate?.(); clearSelection(); }}
        />
      )}
    </>
  );
};

export default BulkSelectableTable;
